import React, { useEffect, useMemo } from "react";
import { Edit } from "@refinedev/antd";
import { useShow, useUpdate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import type { HttpError } from "@refinedev/core";
import { Form, message } from "antd";

import type { CampaignResponse } from "../../api-client";
import type { CampaignFormValues } from "./components/CampaignForm";
import { CampaignForm } from "./components/CampaignForm";

export const CampaignEdit: React.FC = () => {
  const [form] = Form.useForm<CampaignFormValues>();
  const navigate = useNavigate();
  const { query } = useShow<CampaignResponse>();
  const campaign = query.data?.data;
  const { mutate: updateCampaign, isLoading } =
    useUpdate<CampaignResponse, HttpError, CampaignFormValues>();
  const [messageApi, contextHolder] = message.useMessage();

  const initialValues = useMemo<CampaignFormValues | undefined>(() => {
    if (!campaign) {
      return undefined;
    }

    return {
      title: campaign.title,
      rule_code: campaign.rule_code,
      message_template: campaign.message_template ?? undefined,
      image_data: campaign.image_data ?? undefined,
      auto_generate_tasks: campaign.auto_generate_tasks ?? true,
    };
  }, [campaign]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [form, initialValues]);

  const handleSubmit = (values: CampaignFormValues) => {
    if (!campaign?.id) {
      messageApi.error("Кампания не найдена");
      return;
    }

    updateCampaign(
      {
        resource: "campaigns",
        id: campaign.id,
        values,
      },
      {
        onSuccess: ({ data }) => {
          const campaignId = data?.id ?? campaign.id;

          messageApi.success("Кампания успешно обновлена");
          navigate(`/campaigns/show/${campaignId}`);
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.detail ??
            error?.message ??
            "Не удалось обновить кампанию";

          messageApi.error(errorMessage);
        },
      },
    );
  };

  return (
    <Edit
      title="Редактирование кампании"
      isLoading={query.isLoading || isLoading}
      saveButtonProps={{
        onClick: () => form.submit(),
        loading: isLoading,
      }}
    >
      {contextHolder}
      <CampaignForm
        onSubmit={handleSubmit}
        formInstance={form}
        initialValues={initialValues}
        campaignStatus={campaign?.status}
      />
    </Edit>
  );
};

export default CampaignEdit;
